<script setup>
import { LControlLayers, LGeoJson, LMap, LTileLayer, LImageOverlay } from "@vue-leaflet/vue-leaflet"
import "leaflet/dist/leaflet.css"
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import LFullScreen from "vue-leaflet-fullscreen"
import "vue-leaflet-fullscreen/dist/style.css"
import axios from '@/helpers/axios'
import Loader from '@/views/Loader.vue'
import moment from "moment";
import ChartJsLineChart from '@/views/satellite/ChartJsLineChart.vue'
import Breadcrumbs from '@/views/general/Breadcrumbs.vue'
import SoilCard from '@/views/satellite/SoilCard.vue'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'

definePage({
  meta: {
    layout: 'default',
    role: 'user'
  },
})

const { t } = useI18n()
const route = useRoute()
const uuid = route.params.uuid
const map = ref(null)
const zoom = ref(17)

const isInfoDialogVisible = ref(false)

const rowPerPage = ref(5)
const currentPage = ref(1)
const totalPage = ref(1)
const key = ref('ndvi')
const keys = ref([])
const totalItems = ref(0)
const entries = ref([])

const selectedEntry = ref(null)

const accessToken = ref('pk.eyJ1IjoicmVuYXRvbXMiLCJhIjoiY2pzcDNpaDdlMHFvcTQ5cWx3eXJlMDZ2eiJ9.3GsYRV5ahGFqCpb01u7qFw')
const urlOutdoors = ref('https://api.mapbox.com/styles/v1/mapbox/outdoors-v11/tiles/{z}/{x}/{y}?access_token=' + accessToken.value)
const urlSattelite = ref('https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=' + accessToken.value)

const mapRef = ref()

const geojsonStyle = ref({
  //weight: 2,
  //color: "#ECEFF1",
  //opacity: 1,
  //fillColor: "#3388FF",
  fillOpacity: 0
})

const bread = ref([
  {
    text: t('satellite.title'),
    link: { name: 'satellite' }
  },
  {
    text: '...'
  }
])

function onMapReady(m) {
  mapRef.value = m
  m.fitBounds(map.value.bounds)
}


const getIndexes = () => {
  axios.get('/satellite/indexes').then(response => {
    const items = []

    response.data.data.forEach(element => {
      items.push({
        key: element.key,
        name: t('satellite.indexes.' + element.key) + ' - ' + t('satellite.indexes.' + element.key + '_long')
      })
    });
    items.reverse()
    keys.value = items
  }).catch(e => {
    console.error(e)
  })
}

getIndexes()

const getData = () => {
  axios.get('/satellite/' + uuid).then(response => {
    map.value = response.data
  }).catch(e => {
    console.error(e)
  })
}

getData()

const getEntries = (page) => {
  axios.get('/satellite/' + uuid + '/entries', {
    params: {
      perPage: rowPerPage.value,
      currentPage: currentPage.value,
      key: key.value,
    },
  }).then(response => {
    entries.value = entries.value.concat(response.data.data)
    totalPage.value = response.data.meta.last_page
    totalItems.value = response.data.meta.total
    if (entries.value.length > 0) {
      selectedEntry.value = entries.value[0]
    } else {
      selectedEntry.value = null
    }
  }).catch(e => {
    console.error(e)
  })
}

getEntries()

const loadMore = () => {
  if (currentPage.value < totalPage.value) {
    currentPage.value++
    getEntries()
  }
}

const selectEntry = (entry) => {
  selectedEntry.value = entry
  let element = document.getElementById("map");
  element.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
}

watch(key, () => {
  currentPage.value = 1
  entries.value = []
  getEntries()
})


watch(map, () => {
  bread.value = [
    {
      text: t('satellite.title'),
      link: { name: 'satellite' }
    },
    {
      text: map.value.name
    }
  ]
})
</script>

<template>
  <div>
    <Breadcrumbs :items="bread"/>

    <Loader
      v-if="map === null"
      class="mt-4"
    />
    <VRow v-else class="match-height mb-4">

      <VCol
        md="6"
        lg="8"
        cols="12"
      >
        <VCard
          class="overflow-visible pa-4"
          style="border-radius: 16px;"
          elevation="2"
        >
          <!-- Header -->
          <div class="d-flex justify-space-between align-start mb-4">
            <div>
              <h4 class="text-h5 font-weight-semibold mb-1 text-high-emphasis">
                {{ map.name }}
              </h4>
              <span
                class="text-caption px-2 py-1 rounded-lg text-white"
                :class="map.active ? 'bg-primary' : 'bg-warning'"
              >
                {{ map.active ? $t('layout.active') : $t('layout.inactive') }}
              </span>
            </div>
            <VBtn
              icon
              size="small"
              variant="tonal"
              color="primary"
              @click="isInfoDialogVisible = true"
            >
              <VIcon icon="tabler-info-circle" size="x-large" />
              <VTooltip activator="parent" location="top">
                {{ $t('satellite.more_info') }}
              </VTooltip>
            </VBtn>
          </div>

          <!-- Middle section -->
          <div class="mb-2">
            <VRow>
              <VCol>
                <div class="d-flex align-center mb-1">
                  <VIcon icon="tabler-ruler-measure" size="18" class="me-2 text-primary" />
                  <span class="text-body-2 text-medium-emphasis">
                    {{ $t('satellite.area') }}: {{ map.area }} ha
                  </span>
                </div>
              </VCol>
              <VCol>
                <div class="d-flex align-center mb-1">
                  <VIcon icon="tabler-calendar" size="18" class="me-2 text-primary" />
                  <span class="text-body-2 text-medium-emphasis">
                    {{ $t('satellite.last_data') }}: {{ moment.utc(map.last_data).local().format('Y-MM-DD') }}
                  </span>
                </div>
              </VCol>
            </VRow>

            <AppSelect
              v-model="key"
              :items="keys"
              item-title="name"
              item-value="key"
              :label="$t('satellite.index')"
              :placeholder="$t('satellite.select_index')"
              class="mt-4"
            />
          </div>

          <!-- Index Info -->
          <div
            v-if="key"
            class="index-info mt-6 pt-3 border-t"
          >
            <h3 class="text-h5 font-weight-semibold text-high-emphasis mb-1">
              {{ $t('satellite.indexes.' + key ) }}
            </h3>
            <p class="text-body-1 text-medium-emphasis">
              {{ $t('satellite.indexes.' + key + '_description') }}
            </p>
          </div>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        lg="4"
        md="6"
      >
        <SoilCard :parcel="map" />
      </VCol>


      
    </VRow>
      
    <VCard
      id="map"
      class="mb-4 pa-0 overflow-visible"
      elevation="2"
      outlined
      style="border-radius: 16px;"
    >
      <Loader
        v-if="map === null"
        class="mt-4"
      />
      <VCardText v-else class="pa-0">
        <div
          style="height: 500px; border-radius: 16px; overflow: hidden;"
        >
          <LMap
            :zoom="12"
            :center="map.center"
            :max-bounds="map.bounds"
            :use-global-leaflet="false"
            @ready="onMapReady"
          >
            <LFullScreen :map-ref="mapRef" />
            <LImageOverlay
              v-if="selectedEntry"
              :url="selectedEntry.image"
              :bounds="map.bounds"
            />
            <LTileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              layer-type="base"
              name="OpenStreetMap"
              :visible="false"
            />
            <LTileLayer
              :url="urlSattelite"
              layer-type="base"
              name="Mapbox Satellite"
              visible
            />
            <LTileLayer
              :url="urlOutdoors"
              layer-type="base"
              name="Mapbox Outdoors"
              :visible="false"
            />
            <LGeoJson
              v-if="map.geojson"
              :geojson="map.geojson"
              :name="$t('satellite.parcel')"
              layer-type="overlay"
              :options-style="geojsonStyle"
            />
            <LControlLayers
              :collapsed="false"
              position="topright"
            />
          </LMap>
        </div>
      </VCardText>
    </VCard>

    <VRow v-if="entries.length > 0" class="match-height">

      <VCol
        md="6"
        lg="6"
        cols="12"
      >
        <VCard
          class="overflow-visible pa-4"
          style="border-radius: 16px; height: 400px;"
          elevation="2"
        >
            <ChartJsLineChart
              v-if="key !== 'truecolor' && key !== 'falsecolor'"
              :entries="entries"
              :title="key"
              height="100%"
            />
            <div v-else class="text-center py-10">
              <VIcon icon="tabler-database-off" size="48" color="grey" class="mb-3" />
              <h4 class="text-h6 font-weight-medium text-high-emphasis mb-1">
                {{ $t('layout.no_data') }}
              </h4>
            </div>
        </VCard>

      </VCol>

      <VCol
        cols="12"
        lg="6"
        md="6"
      >
        <VCard
          class="overflow-hidden pa-4 d-flex flex-column"
          style="border-radius: 16px; height: 100%;"
          elevation="2"
        >
          <!-- When entries exist -->
          <div v-if="entries.length > 0" class="d-flex flex-column flex-grow-1">
            <PerfectScrollbar
              :options="{ wheelPropagation: false }"
              class="overflow-y-auto"
              style="max-height: 400px;"
            >
              <!-- Each entry card -->
              <div v-for="item in entries" :key="item.uuid" class="mb-3">
                <VCard
                  class="pa-4 cursor-pointer"
                  variant="outlined"
                  :color="selectedEntry.uuid === item.uuid ? 'light-secondary' : undefined"
                  @click="selectEntry(item)"
                >
                  <!-- Header -->
                  <div class="d-flex justify-space-between align-start mb-4">
                    <div>
                      <h4 class="text-h6 font-weight-semibold mb-1 text-high-emphasis">
                        {{ moment.utc(item.sampled_at).local().format('Y-MM-DD') }}
                      </h4>
                      <span class="text-caption px-2 py-1 rounded-lg bg-primary text-white">
                        {{ item.type }}
                      </span>
                    </div>

                    <VBtn
                      icon
                      size="small"
                      variant="tonal"
                      color="primary"
                      @click.stop="selectEntry(item)"
                    >
                      <VIcon icon="tabler-eye" size="x-large" />
                      <VTooltip activator="parent" location="top">
                        {{ $t('layout.view') }}
                      </VTooltip>
                    </VBtn>
                  </div>

                  <!-- Data row -->
                  <div class="d-flex flex-column-reverse flex-md-row align-center">
                    <div v-if="key !== 'truecolor' && key !== 'falsecolor'">
                      <span class="me-2">{{ $t('satellite.min') }}</span>
                      <VChip label color="secondary" class="me-3">
                        {{ Math.round(item.min * 100) / 100 }}
                      </VChip>

                      <span class="me-2">{{ $t('satellite.max') }}</span>
                      <VChip label color="secondary" class="me-3">
                        {{ Math.round(item.max * 100) / 100 }}
                      </VChip>

                      <span class="me-2">{{ $t('satellite.average') }}</span>
                      <VChip label color="light-primary">
                        {{ Math.round(item.mean * 100) / 100 }}
                      </VChip>
                    </div>

                    <div class="ma-auto">
                      <VImg :width="100" :src="item.image" class="rounded" />
                    </div>
                  </div>
                </VCard>
              </div>

              <!-- Load more button -->
              <div
                v-if="currentPage < totalPage"
                class="d-flex flex-column align-center justify-center mt-4"
              >
                <VBtn color="primary" @click="loadMore">
                  {{ $t('layout.load_more') }}
                </VBtn>
              </div>
            </PerfectScrollbar>
          </div>

          <!-- No data -->
          <div
            v-else
            class="d-flex flex-column align-center justify-center text-center flex-grow-1 py-10"
          >
            <VIcon icon="tabler-database-off" size="48" color="grey" class="mb-3" />
            <h4 class="text-h6 font-weight-medium text-high-emphasis mb-1">
              {{ $t('layout.no_data') }}
            </h4>
          </div>
        </VCard>


      </VCol>

    </VRow>
    
    <VDialog
      v-model="isInfoDialogVisible"
      width="500"
    >
      <!-- Dialog close btn -->
      <DialogCloseBtn @click="isDialogVisible = !isDialogVisible" />

      <!-- Dialog Content -->
      <VCard title="Privacy Policy">
        <VCardText>
          {{ $t('satellite.disclaimer' )}}
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>
